import { useEffect, useRef } from 'react';
import { useMutation, useQueries, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ordersClient, paymentHoldCode, type OrderStatus } from '@/lib/orders-client';
import {
  orderBoardClient,
  type FulfillmentType,
  type OrderBoardCard,
  type OrderBoardQuery,
} from '@/lib/order-board-client';
import type { SellChannel } from '@/types/sell-channel';
import { notificationStream } from '@/services/notification-stream';
import type { LocationResponseDto } from '@/types/brand';
import { laneIdForStatus, type BoardLane, type BoardLaneId } from './board-lanes';
import { businessDayStart } from './business-day';

/** Root of every lane query key, so one call can invalidate the whole board. */
export const BOARD_QUERY_ROOT = 'order-board';

/** Lanes refetch on their own as a backstop for a dropped SSE connection; pauses while tab hidden. */
export const LANE_REFETCH_INTERVAL_MS = 20_000;

/** A lane is one page deep — the operator works the head of the queue, not the archive. */
const LANE_PAGE_SIZE = 50;

/** SSE arrives in bursts (a POS push moves two statuses); collapse them into one refetch. */
const STREAM_REFETCH_DEBOUNCE_MS = 400;

/**
 * How far back the board reads, anchored to the START OF THE BUSINESS DAY (see `businessDayStart`)
 * rather than midnight: `today` is everything since the branches opened, `48h` adds the previous
 * day, `7d` the previous six. `all` = unbounded, the old behaviour.
 */
export type BoardWindow = 'today' | '48h' | '7d' | 'all';

export const BOARD_WINDOWS: BoardWindow[] = ['today', '48h', '7d', 'all'];

/** Whole days each window keeps BEFORE the current business day. */
const WINDOW_DAYS_BACK: Record<Exclude<BoardWindow, 'all'>, number> = { today: 0, '48h': 1, '7d': 6 };

/**
 * The `placedFrom` bound for every lane: the current business day's start (the most recent opening
 * of the branches in scope, in their own timezones — local midnight when none has a schedule) minus
 * the window's days. Anchoring on the opening rather than on midnight is what clears yesterday's
 * late-night orders off the board once today's service begins, and not before.
 */
export function windowStart(
  window: BoardWindow,
  locations: readonly LocationResponseDto[] = [],
  now: Date = new Date(),
): string | undefined {
  if (window === 'all') return undefined;
  const start = businessDayStart(locations, now);
  start.setDate(start.getDate() - WINDOW_DAYS_BACK[window]);
  return start.toISOString();
}

export interface BoardFilters {
  /** Undefined = every branch in scope. */
  locationId?: string;
  channel?: SellChannel;
  fulfillmentType?: FulfillmentType;
  /** Aggregate sessions only: the brand filter's selection. */
  brandIds?: string[];
  /**
   * `placedFrom` for EVERY lane (see `windowStart`). Undefined = unbounded. The working lanes are
   * bounded too: an order nobody accepted yesterday is not a queue item today, it is history — the
   * list view is where it can still be found.
   */
  placedFrom?: string;
  /** Identity of the above, for the query keys (the brand filter's own key included). */
  key: string;
}

export interface BoardLaneData {
  cards: OrderBoardCard[];
  total: number;
  hasMore: boolean;
}

export function laneQueryKey(laneId: BoardLaneId, filtersKey: string) {
  return [BOARD_QUERY_ROOT, laneId, filtersKey] as const;
}

function queryFor(lane: BoardLane, filters: BoardFilters): OrderBoardQuery {
  return {
    ...(lane.statuses.length ? { status: lane.statuses } : {}),
    ...(filters.channel ? { channel: [filters.channel] } : {}),
    ...(filters.fulfillmentType ? { fulfillmentType: [filters.fulfillmentType] } : {}),
    ...(filters.locationId ? { locationId: [filters.locationId] } : {}),
    ...(filters.placedFrom ? { placedFrom: filters.placedFrom } : {}),
    ...(lane.handoffCarts ? { handoffCarts: true } : {}),
    brandIds: filters.brandIds,
    limit: LANE_PAGE_SIZE,
  };
}

/**
 * Ordering inside a lane, over the page the API returned (it pages newest-first).
 *
 * - `urgency` (working lanes): the promise we made if there is one, else when the order was placed,
 *   ascending — the card closest to being late sits on top.
 * - `newest` (archive lanes): descending, so the order the operator just finished is the first thing
 *   they see instead of the oldest row of the page.
 */
export function rankCards(cards: OrderBoardCard[], order: BoardLane['rank'] = 'urgency'): OrderBoardCard[] {
  if (order === 'newest') {
    return [...cards].sort((a, b) => placedAtOf(b) - placedAtOf(a));
  }
  return [...cards].sort((a, b) => dueAt(a) - dueAt(b));
}

function placedAtOf(card: OrderBoardCard): number {
  return new Date(card.placedAt).getTime();
}

function dueAt(card: OrderBoardCard): number {
  return new Date(card.promisedAt ?? card.placedAt).getTime();
}

/** One lane = one request. The carts lane reads its own endpoint (drafts are not a placed status). */
function laneQueryOptions(lane: BoardLane, filters: BoardFilters) {
  return {
    queryKey: laneQueryKey(lane.id, filters.key),
    queryFn: async (): Promise<BoardLaneData> => {
      const query = queryFor(lane, filters);
      const response =
        lane.id === 'carts' ? await orderBoardClient.listCarts(query) : await orderBoardClient.listLane(query);
      return {
        cards: rankCards(response.data, lane.rank),
        total: response.pagination.total,
        hasMore: response.pagination.hasMore,
      };
    },
    refetchInterval: LANE_REFETCH_INTERVAL_MS,
  };
}

/**
 * Every lane, in board order: N columns cost N requests and nothing more. One `useQueries` rather
 * than a hook per column so the lanes can be rendered from a list without breaking the rules of
 * hooks, and so the header can count what the whole board is holding.
 */
export function useBoardLanes(lanes: BoardLane[], filters: BoardFilters) {
  return useQueries({ queries: lanes.map((lane) => laneQueryOptions(lane, filters)) });
}

/**
 * Move a card between two lanes' caches — the optimistic half of a status change.
 *
 * When the destination lane has never resolved (its query is still loading) there is nowhere to put
 * the card: removing it from the source anyway would make it vanish from the board entirely until
 * the settle refetch lands. So in that case nothing is moved and the destination lane is invalidated
 * instead — it reloads with the card in it.
 */
function moveCardInCache(
  queryClient: QueryClient,
  lanes: BoardLane[],
  filtersKey: string,
  card: OrderBoardCard,
  nextStatus: OrderStatus,
): void {
  const fromLane = laneIdForStatus(card.status, lanes);
  const toLane = laneIdForStatus(nextStatus, lanes);
  if (!toLane) return;

  const destination = queryClient.getQueryData<BoardLaneData>(laneQueryKey(toLane, filtersKey));
  if (!destination) {
    void queryClient.invalidateQueries({ queryKey: laneQueryKey(toLane, filtersKey) });
    return;
  }

  if (fromLane && fromLane !== toLane) {
    queryClient.setQueryData<BoardLaneData>(laneQueryKey(fromLane, filtersKey), (previous) =>
      previous
        ? {
            ...previous,
            cards: previous.cards.filter((c) => c.orderId !== card.orderId),
            total: Math.max(0, previous.total - 1),
          }
        : previous,
    );
  }

  queryClient.setQueryData<BoardLaneData>(laneQueryKey(toLane, filtersKey), (previous) => {
    if (!previous) return previous;
    const moved = { ...card, status: nextStatus };
    const without = previous.cards.filter((c) => c.orderId !== card.orderId);
    const already = without.length !== previous.cards.length;
    return {
      ...previous,
      cards: rankCards([...without, moved], lanes.find((lane) => lane.id === toLane)?.rank),
      total: already ? previous.total : previous.total + 1,
    };
  });
}

export interface BoardMoveVariables {
  card: OrderBoardCard;
  status: OrderStatus;
  /** Only ever set for a move to `cancelled`, which the board confirms first. */
  cancelledReason?: string;
}

/**
 * The board's status change: optimistic (the card lands in its new lane before the request answers)
 * with a rollback and a toast when the write fails, then a refetch of the whole board so the lanes
 * end up on what the server actually holds.
 */
export function useBoardMove(lanes: BoardLane[], filtersKey: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('orders');

  return useMutation({
    mutationFn: ({ card, status, cancelledReason }: BoardMoveVariables) =>
      ordersClient.updateStatus(card.orderId, status, cancelledReason),
    onMutate: async ({ card, status }) => {
      await queryClient.cancelQueries({ queryKey: [BOARD_QUERY_ROOT] });
      const snapshot = queryClient.getQueriesData<BoardLaneData>({ queryKey: [BOARD_QUERY_ROOT] });
      moveCardInCache(queryClient, lanes, filtersKey, card, status);
      return { snapshot };
    },
    onError: (error, _variables, context) => {
      context?.snapshot.forEach(([key, data]) => queryClient.setQueryData(key, data));
      console.error('Failed to move order on the board:', error);
      // A cancel refused while the payment settles says so — "could not move" would invite a retry.
      const holdCode = paymentHoldCode(error);
      toast.error(holdCode ? t(`errors.${holdCode}`) : t('board.move.failed'));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [BOARD_QUERY_ROOT] });
    },
  });
}

/** Which cached lane currently holds this order — the lane the change is moving it OUT of. */
function laneHoldingOrder(
  queryClient: QueryClient,
  lanes: BoardLane[],
  filtersKey: string,
  orderId: string,
): BoardLaneId | null {
  for (const lane of lanes) {
    const data = queryClient.getQueryData<BoardLaneData>(laneQueryKey(lane.id, filtersKey));
    if (data?.cards.some((card) => card.orderId === orderId)) return lane.id;
  }
  return null;
}

/**
 * Keep the board live off the existing notifications stream: `order.board.changed` says WHICH order
 * moved and WHERE it is now, and the board refetches only the lanes that can have changed — no card
 * payload, so this can never drift from what `GET /orders/board` returns.
 *
 * "Only the lanes that can have changed" means the lane of the NEW status, the lane whose cache
 * still holds the order (where it is leaving from), and the carts lane when the event is about a
 * conversation-less draft appearing or being abandoned. Invalidating the board root instead would
 * cost six requests per event on a brand doing hundreds of status changes an hour, most of them for
 * lanes that cannot possibly have moved.
 *
 * The handoff events (`conversation_needs_intervention` when the agent asks for a human,
 * `conversation_switched_to_manual` when one takes over and the handoff clears) refetch the attention
 * lane: that is where a cart whose chat is waiting for a human appears and disappears, and no
 * `order.board.changed` fires for it — the order itself did not move.
 *
 * Events for another branch than the pinned one are dropped; the lanes' own interval covers anything
 * the stream misses.
 */
export function useBoardStream(lanes: BoardLane[], filters: BoardFilters) {
  const queryClient = useQueryClient();
  const pinnedLocationId = filters.locationId;
  const filtersKey = filters.key;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Lanes touched since the last flush: a burst (a POS push moves two statuses) is collapsed into
  // one refetch per affected lane rather than one refetch of everything.
  const pending = useRef(new Set<BoardLaneId>());

  useEffect(() => {
    const affected = pending.current;
    affected.clear();

    const flush = () => {
      if (affected.size === 0) return;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const touched = [...affected];
        affected.clear();
        touched.forEach((laneId) => {
          void queryClient.invalidateQueries({ queryKey: laneQueryKey(laneId, filtersKey) });
        });
      }, STREAM_REFETCH_DEBOUNCE_MS);
    };

    const unsubscribe = notificationStream.subscribe((payload) => {
      if (payload.type === 'conversation_needs_intervention' || payload.type === 'conversation_switched_to_manual') {
        // A handoff opened or was taken over: the attention lane's cart set may have changed.
        // Conversation events carry no branch, so a pinned board refetches too — one lane, debounced.
        affected.add('attention');
        flush();
        return;
      }
      if (payload.type !== 'order.board.changed') return;
      const eventLocationId = payload.data?.locationId;
      if (pinnedLocationId && eventLocationId && eventLocationId !== pinnedLocationId) return;

      const status = payload.data?.status as OrderStatus | undefined;
      const orderId = payload.data?.orderId;

      const destination = status ? laneIdForStatus(status, lanes) : null;
      if (destination) affected.add(destination);

      const origin = orderId ? laneHoldingOrder(queryClient, lanes, filtersKey, orderId) : null;
      if (origin) affected.add(origin);

      // A cart that nobody is talking to: it enters the carts lane as a draft and leaves it when it
      // is abandoned — or superseded by the cart the customer built on the web (D17). Neither
      // retired status belongs to a lane, so the origin lookup alone would miss it.
      if (
        payload.data?.hasConversation === false &&
        (status === 'draft' || status === 'abandoned' || status === 'superseded')
      ) {
        affected.add('carts');
      }

      flush();
    });

    return () => {
      unsubscribe();
      if (timer.current) clearTimeout(timer.current);
      affected.clear();
    };
  }, [queryClient, lanes, pinnedLocationId, filtersKey]);
}
